import { api } from './client';

type Envelope<T> = { data: T };

export type TemplateRecipientSlot = {
  id: string;
  name: string;
  email: string | null;
  role: 'signer' | 'approver' | 'cc' | 'witness' | 'for_info';
  signingOrder: number;
  authMethod: string;
};

export type TemplateView = {
  id: string;
  title: string;
  message: string | null;
  workflowType: 'sequential' | 'parallel' | 'hybrid';
  isShared: boolean;
  triggerType: string | null;
  triggerDept: string | null;
  triggerForm: string | null;
  createdAt: string;
  updatedAt: string;
  creator: { id: string; firstName: string; lastName: string; email: string };
  documents: Array<{
    id: string;
    originalName: string;
    pagesCount: number;
    fileSize: string;
    orderIndex: number;
  }>;
  recipients: TemplateRecipientSlot[];
};

export const templatesApi = {
  async list(): Promise<TemplateView[]> {
    const { data } = await api.get<Envelope<TemplateView[]>>('/templates');
    return data.data;
  },

  async match(codes: { type?: string; dept?: string; form?: string }): Promise<TemplateView[]> {
    const { data } = await api.get<Envelope<TemplateView[]>>('/templates/match', {
      params: codes,
    });
    return data.data;
  },

  async get(uuid: string): Promise<TemplateView> {
    const { data } = await api.get<Envelope<TemplateView>>(`/templates/${uuid}`);
    return data.data;
  },

  async update(
    uuid: string,
    input: {
      title?: string;
      message?: string;
      triggerType?: string | null;
      triggerDept?: string | null;
      triggerForm?: string | null;
    },
  ): Promise<TemplateView> {
    const { data } = await api.patch<Envelope<TemplateView>>(`/templates/${uuid}`, input);
    return data.data;
  },

  async fromEnvelope(envelopeUuid: string, input: { title?: string; message?: string } = {}): Promise<TemplateView> {
    const { data } = await api.post<Envelope<TemplateView>>(
      `/templates/from-envelope/${envelopeUuid}`,
      input,
    );
    return data.data;
  },

  async instantiate(
    uuid: string,
    body: {
      title?: string;
      message?: string;
      recipients: Array<{ templateRecipientId: string; name: string; email: string }>;
    },
  ): Promise<{ envelopeId: string }> {
    const { data } = await api.post<Envelope<{ envelopeId: string }>>(
      `/templates/${uuid}/instantiate`,
      body,
    );
    return data.data;
  },

  async delete(uuid: string): Promise<void> {
    await api.delete(`/templates/${uuid}`);
  },
};
